import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from '../../core/services/auth.service';
import { ApiService } from '../../core/services/api.service';
import { CenterService } from '../../core/services/center.service';
import { ConfigurationService } from '../../core/services/configuration.service';
import { ONBOARD_URL } from '../../core/constants/api.constants';
import { AppDatePipe } from '../../core/pipes/app-date.pipe';
import { DateFormatService } from '../../core/services/date-format.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, AppDatePipe],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  licenseWarning = '';
  showPassword = false;
  currentYear = new Date().getFullYear();

  // ── Onboard modal state ──
  showOnboardModal = false;
  onboardView: 'login' | 'dashboard' | 'add-subscriber' | 'success' | 'subscribers' | 'change-password' = 'login';
  creatorLoginForm!: FormGroup;
  subscriberForm!: FormGroup;
  creatorToken = '';
  creatorName = '';
  onboardLoading = false;
  onboardError = '';
  stats: { total: number; suspended: number; renewalsDue: number; renewalDays: number } | null = null;
  renewalDays = 15;
  newAccessCode = '';
  newSubscriberName = '';
  showCreatorPassword = false;
  showAdminPassword = false;

  // ── Subscriber list state ──
  subscribers: any[] = [];
  subscribersLoading = false;
  subscriberSearch = '';
  actionLoadingMap: Record<string, boolean> = {};

  // ── Renew modal state ──
  showRenewModal = false;
  renewTarget: any = null;
  renewDate = '';
  renewLoading = false;
  renewError = '';
  renewSuccess = '';

  // ── Change password state ──
  changePwdForm!: FormGroup;
  changePwdLoading = false;
  changePwdError = '';
  changePwdSuccess = '';
  showCurrentPwd = false;
  showNewPwd = false;

  // ── Showcase rotator state ──
  showcaseScene = 0;
  showcasePaused = false;
  progressVisible = true;
  private scTimer: any;
  private readonly SC_MS = 5200;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private api: ApiService,
    private centerService: CenterService,
    private router: Router,
    private http: HttpClient,
    private config: ConfigurationService,
    private dateFmt: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      clientCode: ['', [Validators.required]],
      email:      ['', [Validators.required, Validators.email]],
      password:   ['', [Validators.required, Validators.minLength(6)]],
    });

    this.creatorLoginForm = this.fb.group({
      name:     ['', [Validators.required]],
      password: ['', [Validators.required]],
    });

    this.changePwdForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword:     ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
    });

    this.startScTimer();

    this.subscriberForm = this.fb.group({
      clientName:            ['', [Validators.required]],
      contactPerson:         ['', [Validators.required]],
      phone:                 ['', [Validators.required]],
      email:                 ['', [Validators.required, Validators.email]],
      address:               [''],
      subscriptionValidTill: ['', [Validators.required]],
      adminPassword:         ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  ngOnDestroy(): void { this.stopScTimer(); }

  private startScTimer(): void {
    this.stopScTimer();
    this.scTimer = setTimeout(() => {
      if (!this.showcasePaused) {
        this.showcaseScene = (this.showcaseScene + 1) % 3;
        this.resetProgress();
      }
      this.startScTimer();
    }, this.SC_MS);
  }

  private stopScTimer(): void {
    if (this.scTimer) { clearTimeout(this.scTimer); this.scTimer = null; }
  }

  private resetProgress(): void {
    this.progressVisible = false;
    setTimeout(() => { this.progressVisible = true; }, 16);
  }

  setScene(i: number): void {
    this.showcaseScene = i;
    this.resetProgress();
    this.startScTimer();
  }

  onScEnter(): void { this.showcasePaused = true; }
  onScLeave(): void { this.showcasePaused = false; }

  // ── Main login ──
  get clientCode() { return this.loginForm.get('clientCode')!; }
  get email()      { return this.loginForm.get('email')!; }
  get password()   { return this.loginForm.get('password')!; }

  togglePassword(): void { this.showPassword = !this.showPassword; }

  onSubmit(): void {
    if (this.loginForm.invalid) { this.loginForm.markAllAsTouched(); return; }
    this.isLoading = true;
    this.errorMessage = '';

    this.authService.login(this.email.value, this.password.value, this.clientCode.value).subscribe({
      next: (data) => {
        if (data.role?.toLowerCase() === 'runner') {
          this.isLoading = false;
          this.errorMessage = 'Web app access is not available for runner accounts. Please use the mobile app.';
          this.authService.logout();
          return;
        }

        this.dateFmt.refreshFromApi();
        this.api.loginMembershipCheck().subscribe({ error: () => {} });

        const navigate = () => {
          if (data.role?.toLowerCase() === 'manager') {
            this.loadManagerCenters(data.userID);
          } else {
            this.isLoading = false;
            this.router.navigateByUrl('/dashboard');
          }
        };

        const licInfo = this.authService.getLicenseInfo();
        if (licInfo && licInfo.daysRemaining < 0) {
          this.isLoading = false;
          const expired = this.dateFmt.format(licInfo.validTill);
          this.errorMessage = `Subscription expired on ${expired}. Please renew to continue.`;
          this.authService.logout();
        } else if (licInfo && licInfo.daysRemaining <= 15) {
          this.isLoading = false;
          this.licenseWarning = licInfo.daysRemaining === 0
            ? 'Subscription expires today. Please renew immediately.'
            : `Subscription expires in ${licInfo.daysRemaining} day${licInfo.daysRemaining !== 1 ? 's' : ''}. Please renew soon.`;
          setTimeout(navigate, 3500);
        } else {
          navigate();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err?.error?.message || err?.message || 'Login failed. Please try again.';
      }
    });
  }

  private loadManagerCenters(managerId: string): void {
    this.centerService.loadCentersForManager(managerId).subscribe({
      next: (centers) => {
        this.isLoading = false;
        if (!centers || centers.length === 0) {
          this.errorMessage = 'No centers are assigned to your account. Please contact the administrator.';
          this.authService.logout();
          return;
        }
        this.centerService.setCachedCenters(centers);
        if (centers.length === 1) {
          this.centerService.setSelectedCenter(centers[0]);
          this.router.navigateByUrl('/dashboard');
        } else {
          this.router.navigateByUrl('/select-center');
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Could not load center assignments. Please try again.';
        this.authService.logout();
      }
    });
  }

  // ── Onboard modal ──
  get clf() { return this.creatorLoginForm.controls; }
  get sf()  { return this.subscriberForm.controls; }

  openOnboard(): void {
    this.showOnboardModal = true;
    this.onboardView = 'login';
    this.onboardError = '';
    this.creatorLoginForm.reset();
  }

  closeOnboard(): void {
    this.showOnboardModal = false;
    this.creatorToken = '';
    this.creatorName  = '';
    this.stats = null;
    this.onboardView = 'login';
    this.onboardError = '';
    this.subscribers = [];
    this.subscriberSearch = '';
    this.showRenewModal = false;
    this.changePwdError = '';
    this.changePwdSuccess = '';
  }

  onCreatorLogin(): void {
    if (this.creatorLoginForm.invalid) { this.creatorLoginForm.markAllAsTouched(); return; }
    this.onboardLoading = true;
    this.onboardError = '';

    this.http.post<any>(`${this.config.apiBaseUrl}${ONBOARD_URL}/creatorLogin`, { criteria: this.creatorLoginForm.value }).subscribe({
      next: (res) => {
        this.onboardLoading = false;
        if (res?.errorCode !== '000000') {
          this.onboardError = res?.errorDescription || 'Login failed.';
          return;
        }
        this.creatorToken = res.Data?.token;
        this.creatorName  = res.Data?.name || '';
        this.loadStats(true);
      },
      error: (err) => {
        this.onboardLoading = false;
        this.onboardError = err?.error?.errorDescription || err?.message || 'Login failed.';
      }
    });
  }

  loadStats(showLoader: boolean): void {
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.creatorToken}`, authfor: 'web' });
    const params  = new HttpParams().set('criteria', JSON.stringify({ renewalDays: this.renewalDays }));
    if (showLoader) this.onboardLoading = true;

    this.http.get<any>(`${this.config.apiBaseUrl}${ONBOARD_URL}/getStats`, { headers, params }).subscribe({
      next: (res) => {
        if (showLoader) this.onboardLoading = false;
        if (res?.errorCode !== '000000') {
          this.onboardError = res?.errorDescription || 'Could not load stats.';
          return;
        }
        this.stats = res.Data;
        if (showLoader) this.onboardView = 'dashboard';
      },
      error: (err) => {
        if (showLoader) this.onboardLoading = false;
        if (showLoader) this.onboardError = err?.error?.errorDescription || err?.message || 'Could not load stats.';
      }
    });
  }

  onRenewalDaysChange(event: Event): void {
    const val = parseInt((event.target as HTMLInputElement).value, 10);
    this.renewalDays = isNaN(val) || val < 1 ? 15 : val;
    this.loadStats(false);
  }

  showAddSubscriber(): void {
    this.subscriberForm.reset();
    this.onboardError = '';
    this.onboardView = 'add-subscriber';
  }

  backToDashboard(): void {
    this.onboardView = 'dashboard';
    this.onboardError = '';
    this.showRenewModal = false;
    this.renewTarget = null;
    this.loadStats(false);
  }

  onSaveSubscriber(): void {
    if (this.subscriberForm.invalid) { this.subscriberForm.markAllAsTouched(); return; }
    const headers = new HttpHeaders({ Authorization: `Bearer ${this.creatorToken}`, authfor: 'web' });
    this.onboardLoading = true;
    this.onboardError = '';

    const v = this.subscriberForm.value;
    const payload = { ...v, adminName: v.contactPerson, adminEmail: v.email };

    this.http.post<any>(`${this.config.apiBaseUrl}${ONBOARD_URL}/addSubscriber`, { criteria: payload }, { headers }).subscribe({
      next: (res) => {
        this.onboardLoading = false;
        if (res?.errorCode !== '000000') {
          this.onboardError = res?.errorDescription || 'Could not save subscriber.';
          return;
        }
        this.newAccessCode      = res.Data?.clientCode;
        this.newSubscriberName  = this.subscriberForm.get('clientName')?.value;
        this.loadStats(false);
        this.onboardView = 'success';
      },
      error: (err) => {
        this.onboardLoading = false;
        this.onboardError = err?.error?.errorDescription || err?.message || 'Could not save subscriber.';
      }
    });
  }

  // ── Private HTTP helpers ──
  private _obHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${this.creatorToken}`, authfor: 'web' });
  }

  private _obGet(path: string, criteria: Record<string, any> = {}): any {
    const params = new HttpParams().set('criteria', JSON.stringify(criteria));
    return this.http.get<any>(`${this.config.apiBaseUrl}${ONBOARD_URL}/${path}`, { headers: this._obHeaders(), params });
  }

  private _obPost(path: string, body: Record<string, any> = {}): any {
    return this.http.post<any>(`${this.config.apiBaseUrl}${ONBOARD_URL}/${path}`, { criteria: body }, { headers: this._obHeaders() });
  }

  // ── Subscriber list ──
  get filteredSubscribers(): any[] {
    const q = this.subscriberSearch.toLowerCase().trim();
    if (!q) return this.subscribers;
    return this.subscribers.filter(s =>
      (s.clientName || '').toLowerCase().includes(q) ||
      (s.contactPerson || '').toLowerCase().includes(q) ||
      (s.phone || '').includes(q) ||
      (s.clientCode || '').toLowerCase().includes(q)
    );
  }

  showSubscribersList(): void {
    this.subscriberSearch = '';
    this.onboardError = '';
    this.onboardView = 'subscribers';
    this.loadSubscribers();
  }

  loadSubscribers(): void {
    this.subscribersLoading = true;
    this._obGet('getSubscribers').subscribe({
      next: (res: any) => {
        this.subscribersLoading = false;
        if (res?.errorCode !== '000000') { this.onboardError = res?.errorDescription || 'Could not load subscribers.'; return; }
        this.subscribers = res.Data || [];
      },
      error: (err: any) => {
        this.subscribersLoading = false;
        this.onboardError = err?.error?.errorDescription || err?.message || 'Could not load subscribers.';
      }
    });
  }

  toggleStatus(sub: any): void {
    const newStatus = sub.isActive === false ? true : false;
    this.actionLoadingMap = { ...this.actionLoadingMap, [sub.clientCode]: true };
    this._obPost('toggleSubscriberStatus', { clientCode: sub.clientCode, isActive: newStatus }).subscribe({
      next: (res: any) => {
        this.actionLoadingMap = { ...this.actionLoadingMap, [sub.clientCode]: false };
        if (res?.errorCode !== '000000') { return; }
        const idx = this.subscribers.findIndex(s => s.clientCode === sub.clientCode);
        if (idx >= 0) this.subscribers[idx] = { ...this.subscribers[idx], isActive: newStatus };
      },
      error: () => { this.actionLoadingMap = { ...this.actionLoadingMap, [sub.clientCode]: false }; }
    });
  }

  // ── Renew modal ──
  openRenewModal(sub: any): void {
    this.renewTarget  = sub;
    this.renewError   = '';
    this.renewSuccess = '';
    const d = sub.subscriptionValidTill ? new Date(sub.subscriptionValidTill) : new Date();
    this.renewDate = d.toISOString().substring(0, 10);
    this.showRenewModal = true;
  }

  closeRenewModal(): void {
    this.showRenewModal = false;
    this.renewTarget = null;
  }

  confirmRenew(): void {
    if (!this.renewDate) { this.renewError = 'Please select a date.'; return; }
    this.renewLoading = true;
    this.renewError   = '';
    this._obPost('renewSubscriber', { clientCode: this.renewTarget.clientCode, subscriptionValidTill: this.renewDate }).subscribe({
      next: (res: any) => {
        this.renewLoading = false;
        if (res?.errorCode !== '000000') { this.renewError = res?.errorDescription || 'Could not renew.'; return; }
        const idx = this.subscribers.findIndex(s => s.clientCode === this.renewTarget.clientCode);
        if (idx >= 0) this.subscribers[idx] = { ...this.subscribers[idx], subscriptionValidTill: new Date(this.renewDate), isActive: true };
        this.renewSuccess = 'Subscription renewed successfully.';
        setTimeout(() => this.closeRenewModal(), 1500);
      },
      error: (err: any) => {
        this.renewLoading = false;
        this.renewError = err?.error?.errorDescription || err?.message || 'Could not renew subscription.';
      }
    });
  }

  // ── Change password ──
  showChangePwd(): void {
    this.changePwdForm.reset();
    this.changePwdError = '';
    this.changePwdSuccess = '';
    this.onboardView = 'change-password';
  }

  onChangePwd(): void {
    if (this.changePwdForm.invalid) { this.changePwdForm.markAllAsTouched(); return; }
    const { currentPassword, newPassword, confirmPassword } = this.changePwdForm.value;
    if (newPassword !== confirmPassword) { this.changePwdError = 'New passwords do not match.'; return; }
    this.changePwdLoading = true;
    this.changePwdError   = '';
    this.changePwdSuccess = '';
    this._obPost('changeCreatorPassword', { name: this.creatorName, currentPassword, newPassword }).subscribe({
      next: (res: any) => {
        this.changePwdLoading = false;
        if (res?.errorCode !== '000000') { this.changePwdError = res?.errorDescription || 'Could not change password.'; return; }
        this.changePwdSuccess = 'Password changed successfully.';
        this.changePwdForm.reset();
        setTimeout(() => this.backToDashboard(), 2000);
      },
      error: (err: any) => {
        this.changePwdLoading = false;
        this.changePwdError = err?.error?.errorDescription || err?.message || 'Could not change password.';
      }
    });
  }
}
